import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import type { InputRef } from 'antd';
import dayjs from 'dayjs';
import { Controller, useFormState } from 'react-hook-form';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import {
  AppButton,
  AppCheckbox,
  AppCheckboxFormLabel,
  AppCard,
  AppDivider,
  AppForm,
  AppFormLabel,
  AppRadioGroup,
  AppTextArea,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { useMainHospitalDetail } from '@/hooks/main/useMainHospital';
import { useAddressLookup } from '@/hooks/useAddressLookup';
import {
  halfWidthToFullWidthKana,
  halfWidthToFullWidthAlphaNum,
  fullWidthToHalfWidthAlphaNum,
  fullWidthToHalfWidthNumber,
} from '@/utils';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import {
  INCIDENT_DATE_TYPE_ID,
  R2_SYMPTOM_JOINT_OTHER_ID,
} from '@/constants/consentForm';
import { ConsentFormModals } from './ConsentFormSection/components/ConsentFormModals';
import { DiseaseNameFields } from './ConsentFormSection/components/DiseaseNameFields';
import { MedicalInstitutionFields } from './ConsentFormSection/components/MedicalInstitutionFields';
import { PrintOptionsSection } from './ConsentFormSection/components/PrintOptionsSection';
import { ServiceSpecificTreatmentFields } from './ConsentFormSection/components/ServiceSpecificTreatmentFields';
import { useConsentInjuryNameSync } from './ConsentFormSection/hooks/useConsentInjuryNameSync';
import { useConsentLabelChecks } from './ConsentFormSection/hooks/useConsentLabelChecks';
import { useConsentMasterOptions } from './ConsentFormSection/hooks/useConsentMasterOptions';
import { useConsentTreatmentTermAutoFill } from './ConsentFormSection/hooks/useConsentTreatmentTermAutoFill';
import type {
  ConsentFormSectionProps,
  HospitalModalMode,
} from './ConsentFormSection/types';
import { ensureArray } from './ConsentFormSection/utils';

export type { ConsentFormMode } from './ConsentFormSection/types';

const toDatePickerDisplayValue = (value: unknown): string => {
  if (typeof value !== 'string' || value.trim() === '') {
    return '';
  }
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return convGtJDate(dayjs(trimmed));
  }
  return trimmed;
};

const ConsentFormSection: React.FC<ConsentFormSectionProps> = ({
  control,
  watch,
  setValue,
  serviceType,
  formMode = 'create',
  isContinue = false,
  patient,
  disabled = false,
  onTreatmentReportClick,
}) => {
  const { errors: formErrors, dirtyFields } = useFormState({ control });
  const {
    incidentDateTypeOptions,
    incidentTypeOptions,
    walkingConditionOptions,
    hotTreatmentTypeOptions,
    diseaseNameOptions,
    diseaseNameById,
    treatmentContentOptions,
    symptomMuscleOptions,
    symptomJointOptions,
    massagePartOptions,
    orthomorphiaPartOptions,
    walkingConditionReasonTypeOptions,
  } = useConsentMasterOptions();
  const { allLabelKeys, labelChecks, setLabelChecks } = useConsentLabelChecks({
    serviceType,
    setValue,
    watch,
  });
  const hospitalAddressRef = useRef<InputRef | null>(null);

  const isA2 = serviceType.toUpperCase() === SERVICE_TYPE_A2;
  const incidentDateTypeId = watch('incident_date_type_id');
  const walkingConditionId = watch('walking_condition_id');
  const isNotTransportCost = watch('is_not_transport_cost');
  const isAhakiBothTreatment = watch('is_ahaki_both_treatment');
  const hospitalId = watch('hospital_id');
  const [isHospitalSearchModalOpen, setIsHospitalSearchModalOpen] = useState(false);
  const [isHospitalModalOpen, setIsHospitalModalOpen] = useState(false);
  const [hospitalModalMode, setHospitalModalMode] = useState<HospitalModalMode>('edit');
  const [isAddressSearchModalOpen, setIsAddressSearchModalOpen] = useState(false);

  const { data: hospitalDetailResponse } = useMainHospitalDetail(hospitalId || 0, {
    showNotFoundToast: false,
  });
  const hospital = hospitalDetailResponse?.data || null;

  const doctorOptions = useMemo(() => {
    if (!hospital?.doctors) return [];
    return hospital.doctors.map((d: { name: string; id: number }) => ({
      label: d.name,
      value: d.id,
    }));
  }, [hospital]);

  // If hospital is selected but doctor isn't, auto-pick first doctor.
  // This matches the spec and prevents "doctor dropdown chosen but doctor_name empty" inconsistencies.
  const doctorId = watch('doctor_id');
  useEffect(() => {
    if (!hospitalId) return;
    if (!hospital) return;
    if (doctorId != null) return;
    const doctors = hospital.doctors ?? [];
    if (doctors.length === 0) return;
    setValue(
      'doctor_name',
      fullWidthToHalfWidthAlphaNum(fullWidthToHalfWidthNumber(doctors[0].name)),
      { shouldDirty: true, shouldValidate: true }
    );
  }, [doctorId, hospital, hospitalId, setValue]);

  const { diseaseName, markDiseaseUserAction, markInjuryNameEdited } =
    useConsentInjuryNameSync({
      diseaseNameById,
      isA2,
      setValue,
      watch,
    });
  const symptomJointSel = ensureArray(watch('symptom_joint'));
  const isSymptomJointOtherSelected = symptomJointSel.includes(R2_SYMPTOM_JOINT_OTHER_ID);
  const massagePartsSel = ensureArray(watch('massage_parts')) as string[];
  const orthomorphiaPartsSel = ensureArray(watch('orthomorphia_parts')) as string[];
  const walkingConditionReasonTypeIds = ensureArray(watch('walking_condition_reason_type_id'));
  useConsentTreatmentTermAutoFill({
    dirtyFields,
    isContinue,
    massagePartsSel,
    orthomorphiaPartsSel,
    serviceType,
    setValue,
    watch,
  });
  const currentHealthInsuranceCard = useMemo(() => {
    const cards = patient?.health_insurance_cards ?? [];
    return cards.find(c => c?.is_current === true || c?.is_current === 1) ?? cards[0];
  }, [patient?.health_insurance_cards]);

  const { search: handleHospitalAddressSearch, isPending: isHospitalPostalSearching } =
    useAddressLookup({
      getValues: () => ({
        pc1: watch('hospital_postal_code1') || '',
        pc2: watch('hospital_postal_code2') || '',
        address: watch('hospital_address') || '',
      }),
      applyResult: ({ pc1, pc2, address }) => {
        setValue('hospital_postal_code1', pc1, { shouldDirty: true, shouldValidate: true });
        setValue('hospital_postal_code2', pc2, { shouldDirty: true, shouldValidate: true });
        setValue('hospital_address', address, { shouldDirty: true, shouldValidate: true });
      },
      openModal: () => setIsAddressSearchModalOpen(true),
      showNotFoundWarning: false,
      onAfterApply: () => {
        const nativeInput = hospitalAddressRef.current?.input;
        if (nativeInput) {
          nativeInput.focus();
          nativeInput.setSelectionRange(nativeInput.value.length, nativeInput.value.length);
        }
      },
    });

  const handleHospitalAddressSearchSelect = useCallback(
    (result: { postal_code1: string; postal_code2: string; address: string }) => {
      setValue('hospital_postal_code1', result.postal_code1, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('hospital_postal_code2', result.postal_code2, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('hospital_address', result.address, { shouldDirty: true, shouldValidate: true });
      setIsAddressSearchModalOpen(false);
    },
    [setValue]
  );

  const patientAddress =
    `${currentHealthInsuranceCard?.address1 ?? ''}${currentHealthInsuranceCard?.address2 ?? ''}`.trim();
  const patientName = patient?.current_patient_information?.name ?? '';
  const patientBirthOfDate = patient?.current_patient_information?.date_of_birth
    ? convGtJDate(patient.current_patient_information.date_of_birth)
    : '';

  return (
    <AppCard
      className="bg-[#fdf5e6]"
      style={{
        WebkitBoxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
        boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
      }}
    >
      {/* 1. Print Checkboxes */}
      <PrintOptionsSection
        allLabelKeys={allLabelKeys}
        control={control}
        labelChecks={labelChecks}
        patientAddress={patientAddress}
        patientBirthOfDate={patientBirthOfDate}
        patientName={patientName}
        setLabelChecks={setLabelChecks}
      />

      <AppDivider className="my-0" />

      {/* 2. Consent Information */}
      <div className="py-4">
        <div className="grid grid-cols-1 gap-6">
          <div className="grid grid-cols-[280px_1fr] gap-x-6 gap-y-4 items-start">
            <DiseaseNameFields
              control={control}
              diseaseName={diseaseName}
              diseaseNameOptions={diseaseNameOptions}
              isA2={isA2}
              labelChecks={labelChecks}
              markDiseaseUserAction={markDiseaseUserAction}
              markInjuryNameEdited={markInjuryNameEdited}
              setLabelChecks={setLabelChecks}
              setValue={setValue}
            />

            <div className="flex items-center gap-2">
              <AppFormLabel
                label="発病・負傷原因及び経過"
                required
                className="ml-[24.4px] flex-1"
              />
            </div>
            <div>
              <Controller
                name="incident_cause"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                    <AppTextField
                      {...field}
                      value={field.value || ''}
                      maxLength={100}
                      onBlur={() => {
                        const raw = field.value || '';
                        field.onChange(halfWidthToFullWidthKana(halfWidthToFullWidthAlphaNum(raw)));
                      }}
                      status={error ? 'error' : ''}
                    />
                  </AppForm.Item>
                )}
              />
              <AppTypography.Text type="secondary" className="block mt-1 text-xs">
                ※「発病・負傷原因及び経過」は、100文字以内を推奨します。
              </AppTypography.Text>
            </div>

            <div className="flex items-center gap-2">
              <AppCheckboxFormLabel
                label="発病・負傷年月日"
                required
                checked={!!labelChecks.incident_date}
                onChange={e =>
                  setLabelChecks(prev => ({ ...prev, incident_date: e.target.checked }))
                }
                className="flex-1"
              />
            </div>
            <div>
              <div className="flex flex-wrap items-start gap-4">
                <Controller
                  name="incident_date_type_id"
                  control={control}
                  render={({ field }) => (
                    <AppRadioGroup
                      {...field}
                      className="flex flex-wrap gap-3"
                      options={incidentDateTypeOptions}
                      onChange={e => {
                        const nextTypeId = e?.target?.value as number;
                        field.onChange(nextTypeId);

                        if (nextTypeId === INCIDENT_DATE_TYPE_ID.UNKNOWN) {
                          // 不詳に切り替え → 日付クリア
                          setValue('incident_date', null, {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                          return;
                        }

                        // 既存日付を引き継ぎ、足りない部分を 01 で補完する
                        const current = watch('incident_date');
                        const parsed = current ? dayjs(current) : null;
                        if (!parsed?.isValid()) {
                          // 既存値なし → そのまま（ユーザーが入力する）
                          return;
                        }

                        const y = parsed.year();
                        const m = String(parsed.month() + 1).padStart(2, '0');
                        if (nextTypeId === INCIDENT_DATE_TYPE_ID.YEAR) {
                          setValue('incident_date', `${y}-01-01`, {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                        } else if (nextTypeId === INCIDENT_DATE_TYPE_ID.MONTH) {
                          setValue('incident_date', `${y}-${m}-01`, {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                        } else {
                          // 日付指定: 年・月は維持、日は 01 で補完
                          setValue('incident_date', `${y}-${m}-01`, {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                        }
                      }}
                    />
                  )}
                />
                {incidentDateTypeId !== INCIDENT_DATE_TYPE_ID.UNKNOWN && (
                  <div className="min-w-[200px]">
                    <Controller
                      name="incident_date"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <AppForm.Item
                          validateStatus={error ? 'error' : undefined}
                          help={error?.message}
                        >
                          <RA2NDatePicker
                            value={field.value ?? ''}
                            onChange={val => {
                              if (!val?.trim()) {
                                field.onChange(null);
                                return;
                              }
                              field.onChange(val);
                            }}
                            className="w-full max-w-[200px]"
                            picker={
                              incidentDateTypeId === INCIDENT_DATE_TYPE_ID.YEAR
                                ? 'year'
                                : incidentDateTypeId === INCIDENT_DATE_TYPE_ID.MONTH
                                  ? 'month'
                                  : 'day'
                            }
                            placeholder={
                              incidentDateTypeId === INCIDENT_DATE_TYPE_ID.YEAR
                                ? '年指定'
                                : incidentDateTypeId === INCIDENT_DATE_TYPE_ID.MONTH
                                  ? '年月指定'
                                  : '元号00年00月00日'
                            }
                            error={!!error}
                          />
                        </AppForm.Item>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <AppFormLabel
                label="業務上・外、第三者行為の有無"
                required
                className="ml-[24.4px] flex-1"
              />
            </div>
            <div>
              <Controller
                name="incident_type_id"
                control={control}
                render={({ field }) => (
                  <AppRadioGroup
                    {...field}
                    className="flex flex-wrap gap-3"
                    options={incidentTypeOptions}
                  />
                )}
              />
            </div>

            <div className="flex items-center gap-2">
              <AppCheckboxFormLabel
                label="同意区分"
                checked={!!labelChecks.is_continuation}
                onChange={e =>
                  setLabelChecks(prev => ({ ...prev, is_continuation: e.target.checked }))
                }
                className="flex-1"
              />
            </div>
            <div
              className="flex items-center gap-6"
              title={
                formMode === 'edit'
                  ? '編集画面では同意区分は登録時の内容を表示します（変更はできません）'
                  : undefined
              }
            >
              <AppTypography.Text className="text-base">
                {watch('is_continuation') ? '継続' : watch('is_special') && isA2 ? '継続' : '新規'}
              </AppTypography.Text>
            </div>

            <div className="flex items-center gap-2">
              <AppCheckboxFormLabel
                label="医療機関での診察年月日"
                required
                checked={!!labelChecks.examine_date}
                onChange={e =>
                  setLabelChecks(prev => ({ ...prev, examine_date: e.target.checked }))
                }
                className="flex-1"
              />
            </div>
            <div>
              <Controller
                name="examine_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                    <RA2NDatePicker
                      value={toDatePickerDisplayValue(field.value)}
                      onChange={val => {
                        field.onChange(dateValueToIso(val) || '');
                      }}
                      defaultEra="reiwa"
                      className="w-full max-w-[200px]"
                      placeholder="元号00年00月00日"
                      error={!!error}
                    />
                  </AppForm.Item>
                )}
              />
            </div>

            <div className="flex items-center gap-2">
              <AppFormLabel label="初療年月日" required className="ml-[24.4px] flex-1" />
            </div>
            <div>
              <Controller
                name="first_treatment_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                    <div className="flex flex-col gap-1">
                      <RA2NDatePicker
                        value={toDatePickerDisplayValue(field.value)}
                        onChange={val => {
                          field.onChange(dateValueToIso(val) || '');
                        }}
                        className="w-full max-w-[200px]"
                        placeholder="元号00年00月00日"
                        error={!!error}
                      />
                      <AppTypography.Text className="text-xs">
                        {isA2
                          ? '※初療年月日とは受診者が初めて施術を受けた日付です。'
                          : '※初療年月日とは受診者が初めてマッサージ施術を受けた日付です。'}
                      </AppTypography.Text>
                    </div>
                  </AppForm.Item>
                )}
              />
            </div>

            <div className="col-span-2">
              <AppDivider className="my-0" />
            </div>

            <ServiceSpecificTreatmentFields
              control={control}
              hotTreatmentTypeOptions={hotTreatmentTypeOptions}
              isA2={isA2}
              isNotTransportCost={isNotTransportCost}
              isSymptomJointOtherSelected={isSymptomJointOtherSelected}
              labelChecks={labelChecks}
              massagePartOptions={massagePartOptions}
              massagePartsSel={massagePartsSel}
              orthomorphiaPartOptions={orthomorphiaPartOptions}
              orthomorphiaPartsSel={orthomorphiaPartsSel}
              setLabelChecks={setLabelChecks}
              setValue={setValue}
              symptomJointOptions={symptomJointOptions}
              symptomMuscleOptions={symptomMuscleOptions}
              treatmentContentOptions={treatmentContentOptions}
              walkingConditionId={walkingConditionId}
            />

            <div className="col-span-2">
              <AppDivider className="my-0" />
            </div>

            <div className="flex items-center gap-2">
              <AppCheckboxFormLabel
                label="往療"
                required
                checked={!!labelChecks.walking_condition_id}
                onChange={e =>
                  setLabelChecks(prev => ({ ...prev, walking_condition_id: e.target.checked }))
                }
                className="flex-1"
              />
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <Controller
                  name="walking_condition_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <AppForm.Item
                      validateStatus={error ? 'error' : undefined}
                      help={error?.message}
                      className="mb-0 [&_.ant-form-item-control-input]:items-start [&_.ant-form-item-control-input]:min-h-0"
                    >
                      <AppRadioGroup
                        {...field}
                        className="flex gap-4"
                        onChange={e => {
                          const nextValue = e?.target?.value;
                          field.onChange(nextValue);
                          if (nextValue === 2) {
                            setValue('is_not_transport_cost', false, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                            setValue('is_ahaki_both_treatment', false, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                            setValue('walking_condition_reason_type_id', [], {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                            setValue('walking_condition_reason_other', '', {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                        }}
                        options={walkingConditionOptions}
                      />
                    </AppForm.Item>
                  )}
                />
              </div>
              <div className="flex items-start">
                <Controller
                  name="is_not_transport_cost"
                  control={control}
                  render={({ field }) => (
                    <AppCheckbox
                      {...field}
                      checked={field.value}
                      onChange={e => {
                        const checked = e.target.checked;
                        field.onChange(checked);
                        if (checked) {
                          setValue('is_ahaki_both_treatment', false, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                      disabled={!!isAhakiBothTreatment || walkingConditionId !== 1}
                    >
                      通所にする
                    </AppCheckbox>
                  )}
                />
              </div>
            </div>

            <div className="col-span-2">
              <AppDivider className="my-0" />
            </div>

            <div className="flex items-center gap-2">
              <AppFormLabel label="往療を必要とする理由" required className="ml-[24.4px] flex-1" />
            </div>
            <div>
              <Controller
                name="walking_condition_reason_type_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                    <AppCheckbox.Group
                      {...field}
                      value={ensureArray(field.value)}
                      disabled={walkingConditionId !== 1}
                      className="flex flex-col gap-2"
                      onChange={nextValues => {
                        const prevValues = ensureArray(field.value);
                        const nextArr = ensureArray(nextValues);
                        const hasOtherBefore = prevValues.includes('その他');
                        const hasOtherAfter = nextArr.includes('その他');

                        field.onChange(nextArr);

                        if (hasOtherBefore && !hasOtherAfter) {
                          setValue('walking_condition_reason_other', '', {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                      options={walkingConditionReasonTypeOptions}
                    />
                  </AppForm.Item>
                )}
              />
              <div className="mt-2">
                <Controller
                  name="walking_condition_reason_other"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <AppForm.Item
                      validateStatus={error ? 'error' : undefined}
                      help={error?.message}
                      className="mb-0"
                    >
                      <AppTextField
                        {...field}
                        value={field.value || ''}
                        maxLength={255}
                        disabled={
                          walkingConditionId !== 1 ||
                          !walkingConditionReasonTypeIds?.includes('その他')
                        }
                        status={error ? 'error' : ''}
                      />
                    </AppForm.Item>
                  )}
                />
              </div>
            </div>

            <div className="col-span-2">
              <AppDivider className="my-0" />
            </div>

            <div className="flex items-center gap-2">
              <AppFormLabel label="介護保険の要介護度" className="ml-[24.4px] flex-1" />
            </div>
            <div>
              <Controller
                name="walking_condition_reason_level"
                control={control}
                render={({ field }) => (
                  <AppTextField {...field} value={field.value || ''} maxLength={255} />
                )}
              />
            </div>

            <div className="col-span-2">
              <AppDivider className="my-0" />
            </div>

            <div className="flex items-center gap-2">
              <AppCheckboxFormLabel
                label="注意事項"
                checked={!!labelChecks.remarks}
                onChange={e => setLabelChecks(prev => ({ ...prev, remarks: e.target.checked }))}
                className="flex-1"
              />
            </div>
            <div>
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <AppTextArea {...field} value={field.value || ''} rows={1} maxLength={255} />
                )}
              />
            </div>

            <div className="col-span-2">
              <AppDivider className="my-0" />
            </div>

            <MedicalInstitutionFields
              control={control}
              disabled={disabled}
              doctorOptions={doctorOptions}
              handleHospitalAddressSearch={handleHospitalAddressSearch}
              hospital={hospital}
              hospitalAddressRef={hospitalAddressRef}
              hospitalId={hospitalId}
              isHospitalPostalSearching={isHospitalPostalSearching}
              labelChecks={labelChecks}
              setHospitalModalMode={setHospitalModalMode}
              setIsHospitalModalOpen={setIsHospitalModalOpen}
              setIsHospitalSearchModalOpen={setIsHospitalSearchModalOpen}
              setLabelChecks={setLabelChecks}
              setValue={setValue}
            />

            <div className="col-span-2">
              <AppDivider className="my-0" />
            </div>

            {/* 5. Consent Period */}
            <div className="flex items-center gap-2">
              <AppCheckboxFormLabel
                label="同意日"
                required
                checked={!!labelChecks.consent_date}
                onChange={e =>
                  setLabelChecks(prev => ({ ...prev, consent_date: e.target.checked }))
                }
                className="flex-1"
              />
            </div>
            <div>
              <Controller
                name="consent_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                    <RA2NDatePicker
                      value={toDatePickerDisplayValue(field.value)}
                      onChange={val => field.onChange(dateValueToIso(val) || '')}
                      defaultEra="reiwa"
                      className="w-full max-w-[200px]"
                      placeholder="元号00年00月00日"
                      error={!!error}
                    />
                  </AppForm.Item>
                )}
              />
            </div>

            <div className="flex items-center gap-2">
              <AppFormLabel label="要加療期間" required className="ml-[24.4px] flex-1" />
            </div>
            <div>
              <div className="flex flex-col gap-1">
                {(() => {
                  const fromErr = formErrors.treatment_term_from?.message;
                  const toErr = formErrors.treatment_term_to?.message;
                  const REQUIRED_FROM = '要加療期間開始日は必須です。';
                  const REQUIRED_TO = '要加療期間終了日は必須です。';
                  const isFromRequired = fromErr === REQUIRED_FROM;
                  const isToRequired = toErr === REQUIRED_TO;
                  const spanningError =
                    fromErr && !isFromRequired ? fromErr : toErr && !isToRequired ? toErr : null;

                  return (
                    <>
                      <div className="flex flex-wrap items-start gap-2">
                        <Controller
                          name="treatment_term_from"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <AppForm.Item
                              className="mb-0"
                              validateStatus={error || spanningError ? 'error' : undefined}
                              help={spanningError ? undefined : error?.message}
                            >
                              <RA2NDatePicker
                                value={toDatePickerDisplayValue(field.value)}
                                onChange={val => field.onChange(dateValueToIso(val) || '')}
                                className="w-full max-w-[200px]"
                                placeholder="元号00年00月00日"
                                error={!!error || !!spanningError}
                              />
                            </AppForm.Item>
                          )}
                        />
                        <AppTypography.Text className="mt-1">~</AppTypography.Text>
                        <Controller
                          name="treatment_term_to"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <AppForm.Item
                              className="mb-0"
                              validateStatus={error || spanningError ? 'error' : undefined}
                              help={spanningError ? undefined : error?.message}
                            >
                              <RA2NDatePicker
                                value={toDatePickerDisplayValue(field.value)}
                                onChange={val => field.onChange(dateValueToIso(val) || '')}
                                className="w-full max-w-[200px]"
                                placeholder="元号00年00月00日"
                                error={!!error || !!spanningError}
                              />
                            </AppForm.Item>
                          )}
                        />
                        <div className="flex items-center mt-1 ml-2">
                          <Controller
                            name="is_treatment_complete"
                            control={control}
                            render={({ field }) => (
                              <AppCheckbox
                                {...field}
                                checked={field.value}
                                onChange={e => field.onChange(e.target.checked)}
                              >
                                終了
                              </AppCheckbox>
                            )}
                          />
                        </div>
                      </div>
                      {spanningError && <div className="text-sm text-red-500">{spanningError}</div>}
                    </>
                  );
                })()}
              </div>
            </div>

            {onTreatmentReportClick ? (
              <div className="flex justify-end col-span-2">
                <AppButton
                  type="primary"
                  htmlType="button"
                  className="w-fit"
                  disabled={disabled}
                  onClick={onTreatmentReportClick}
                >
                  施術録
                </AppButton>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ConsentFormModals
        handleHospitalAddressSearchSelect={handleHospitalAddressSearchSelect}
        hospital={hospital}
        hospitalId={hospitalId}
        hospitalModalMode={hospitalModalMode}
        isAddressSearchModalOpen={isAddressSearchModalOpen}
        isHospitalModalOpen={isHospitalModalOpen}
        isHospitalSearchModalOpen={isHospitalSearchModalOpen}
        setIsAddressSearchModalOpen={setIsAddressSearchModalOpen}
        setIsHospitalModalOpen={setIsHospitalModalOpen}
        setIsHospitalSearchModalOpen={setIsHospitalSearchModalOpen}
        setValue={setValue}
        watch={watch}
      />
    </AppCard>
  );
};

export default ConsentFormSection;
